import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ItemWithPath } from '@ousse/shared'
import { api } from '../lib/api'
import { useHousehold } from '../lib/household'
import { photoThumbUrl } from '../lib/photos'
import { SeeIn3DForItem } from '../components/SeeIn3D'
import { ErrorAlert, Field } from '../components/ui'

/** Recherche d'objets par nom / description / marque, avec chemin complet. */
export function SearchPage() {
  const { t } = useTranslation()
  const { current } = useHousehold()
  // `?q=` : recherche lancée depuis l'accueil.
  const [params] = useSearchParams()
  const initial = params.get('q')?.trim() ?? ''
  const [draft, setDraft] = useState(initial)
  const [q, setQ] = useState(initial)
  const [error, setError] = useState<unknown>(null)

  const results = useQuery({
    queryKey: ['search', current?.id, q],
    enabled: Boolean(current?.id) && q.length > 0,
    queryFn: async () => {
      try {
        setError(null)
        return await api.get<{ items: ItemWithPath[]; nextCursor: string | null }>(
          `/households/${current!.id}/items?q=${encodeURIComponent(q)}&limit=50`,
        )
      } catch (e) {
        setError(e)
        throw e
      }
    },
  })

  if (!current) return <p role="status">{t('common.loading')}</p>

  return (
    <>
      <h1>{t('inventory.searchTitle')}</h1>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault()
          setQ(draft.trim())
        }}
      >
        <Field
          label={t('inventory.searchTitle')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('inventory.searchPlaceholder')}
          maxLength={200}
          autoFocus={!initial}
        />
        <button type="submit" className="btn" disabled={!draft.trim()}>
          {t('inventory.searchSubmit')}
        </button>
      </form>
      <ErrorAlert error={error} />
      {!q && <p className="muted">{t('inventory.searchHint')}</p>}
      {q && results.isLoading && <p role="status">{t('common.loading')}</p>}
      {q && results.data && results.data.items.length === 0 && <p>{t('inventory.searchEmpty')}</p>}
      {results.data && results.data.items.length > 0 && (
        <ul className="list">
          {results.data.items.map((item) => {
            const label = item.name?.trim() || t('inventory.untitledItem')
            const pathLabel =
              item.path.length > 0 ? item.path.map((p) => p.name).join(' › ') : t('inventory.uncategorized')
            return (
              <li key={item.id} className="row" style={{ gap: '0.75rem', alignItems: 'center' }}>
                <Link to={`/items/${item.id}`} className="row" style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}>
                  {item.primaryPhotoId ? (
                    <img
                      src={photoThumbUrl(current.id, item.primaryPhotoId)}
                      alt={label}
                      className="thumb small"
                      loading="lazy"
                    />
                  ) : (
                    <span className="thumb-placeholder small" aria-hidden />
                  )}
                  <span>
                    <strong>{label}</strong>
                    <br />
                    <span className="muted">
                      {t('inventory.whereIsIt')} : {pathLabel}
                    </span>
                  </span>
                </Link>
                <SeeIn3DForItem item={item} />
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}
